import React, { useState } from 'react';
import { Users, X, Circle } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useUserStatusStore } from '@/store/useUserStatusStore';

import { OnlineUsersList } from './OnlineUsersList';

/**
 * Floating widget showing online users with Arabic support
 * Appears in bottom-left corner with expand/collapse/minimize functionality
 * Always starts as minimized icon button
 */
export const FloatingOnlineWidget = React.memo(function FloatingOnlineWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(true); // Always start minimized

  // Subscribe to online count from store using the getter function
  const onlineCount = useUserStatusStore((state) => state.getOnlineCount());

  // Minimized state - just a small button
  if (isMinimized && !isOpen) {
    return (
      <div className="fixed bottom-4 left-4 z-40">
        <Button
          onClick={() => {
            setIsOpen(true);
            setIsMinimized(false);
          }}
          className="relative h-14 w-14 rounded-full shadow-lg hover:shadow-xl transition-all"
          size="icon"
        >
          <Users className="h-6 w-6" />
          {onlineCount > 0 && (
            <span className="absolute -top-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full bg-green-500 text-xs font-bold text-white ring-2 ring-white dark:ring-slate-900">
              {onlineCount}
            </span>
          )}
        </Button>
      </div>
    );
  }

  // Collapsed state - button with count and Arabic text
  if (!isOpen) {
    return (
      <div className="fixed bottom-4 left-4 z-40">
        <Button
          onClick={() => setIsOpen(true)}
          className="relative gap-3 shadow-lg hover:shadow-xl transition-all duration-300 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary text-primary-foreground px-5 py-6 rounded-2xl group"
          size="lg"
        >
          <div className="relative">
            <Users className="h-5 w-5 transition-transform group-hover:scale-110" />
            {onlineCount > 0 && (
              <Circle className="h-2 w-2 fill-green-400 text-green-400 absolute -top-0.5 -right-0.5 animate-pulse" />
            )}
          </div>
          <div className="flex flex-col items-start gap-0.5">
            <span className="font-semibold text-base leading-tight">
              المستخدمون المتصلون
            </span>
            <span className="text-xs opacity-90 font-medium">
              {onlineCount} {onlineCount === 1 ? 'مستخدم' : onlineCount === 2 ? 'مستخدمان' : 'مستخدمين'} نشط
            </span>
          </div>
        </Button>
      </div>
    );
  }

  // Expanded state - full widget with improved UI
  return (
    <div
      className={cn(
        'fixed bottom-4 left-4 z-40 w-96 transition-all duration-300',
        'rounded-2xl shadow-2xl border border-border/50 backdrop-blur-sm',
        'animate-in slide-in-from-bottom-5 fade-in-0 bg-background/95',
      )}
    >
      {/* Professional Premium Header - Dark Mode Optimized */}
      <div className="relative overflow-hidden rounded-t-2xl">
        {/* Sophisticated Dark Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-800 via-slate-900 to-slate-950 dark:from-slate-900 dark:via-slate-950 dark:to-black" />

        {/* Premium Accent Gradient */}
        <div className="absolute inset-0 bg-gradient-to-r from-primary/20 via-primary/10 to-transparent opacity-80 dark:opacity-60" />

        {/* Subtle Pattern Overlay */}
        <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '24px 24px'
        }} />

        {/* Ambient Glow Elements */}
        <div className="absolute -top-20 -right-20 h-40 w-40 rounded-full bg-primary/20 dark:bg-primary/30 blur-3xl" />
        <div className="absolute -bottom-10 -left-10 h-32 w-32 rounded-full bg-blue-500/10 dark:bg-blue-400/20 blur-2xl" />

        {/* Content Layer */}
        <div className="relative px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Left Section - Icon & Title */}
            <div className="flex items-center gap-4">
              {/* Professional Icon Container */}
              <div className="relative">
                {/* Glow Ring */}
                <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-primary/40 to-blue-500/40 dark:from-primary/50 dark:to-blue-400/50 blur-lg opacity-75" />

                {/* Icon Box */}
                <div className="relative flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-white/[0.12] to-white/[0.04] dark:from-white/[0.15] dark:to-white/[0.05] border border-white/[0.15] dark:border-white/[0.2] backdrop-blur-xl shadow-xl">
                  <Users className="h-5 w-5 text-white" />

                  {/* Online Pulse Indicator */}
                  {onlineCount > 0 && (
                    <div className="absolute -top-1 -right-1">
                      <div className="relative flex h-4 w-4 items-center justify-center">
                        <div className="absolute inset-0 rounded-full bg-emerald-500 dark:bg-emerald-400 animate-ping opacity-75" />
                        <div className="relative h-3 w-3 rounded-full bg-emerald-500 dark:bg-emerald-400 border-2 border-slate-900" />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Title & Status */}
              <div className="flex flex-col gap-1.5">
                {/* Title Row */}
                <div className="flex items-center gap-2.5">
                  <h3 className="text-lg font-bold text-white tracking-tight">
                    المستخدمون
                  </h3>

                  {/* Online Count Badge */}
                  {onlineCount > 0 && (
                    <div className="flex items-center gap-1.5 rounded-lg bg-emerald-500/20 dark:bg-emerald-400/25 px-2.5 py-1 border border-emerald-500/30 dark:border-emerald-400/40 backdrop-blur-sm">
                      <div className="h-1.5 w-1.5 rounded-full bg-emerald-400 dark:bg-emerald-300 animate-pulse" />
                      <span className="text-xs font-bold text-emerald-100 dark:text-emerald-50 tabular-nums">
                        {onlineCount}
                      </span>
                    </div>
                  )}
                </div>

                {/* Status Text */}
                <div className="flex items-center gap-2 text-xs">
                  <span className="font-medium text-slate-300 dark:text-slate-400">
                    {onlineCount} {onlineCount === 1 ? 'مستخدم' : onlineCount === 2 ? 'مستخدمان' : 'مستخدمين'} متصل
                  </span>
                  <div className="h-1 w-1 rounded-full bg-slate-500 dark:bg-slate-600" />
                  <span className="font-medium text-slate-400 dark:text-slate-500">
                    نشط الآن
                  </span>
                </div>
              </div>
            </div>

            {/* Right Section - Close Button */}
            <button
              onClick={() => {
                setIsOpen(false);
                setIsMinimized(true);
              }}
              className="group flex h-8 w-8 items-center justify-center rounded-lg bg-white/[0.08] dark:bg-white/[0.1] hover:bg-white/[0.15] dark:hover:bg-white/[0.18] border border-white/[0.1] dark:border-white/[0.15] backdrop-blur-sm transition-all duration-200 hover:scale-105"
              aria-label="تصغير"
            >
              <X className="h-4 w-4 text-slate-300 dark:text-slate-400 group-hover:text-white transition-colors" />
            </button>
          </div>
        </div>

        {/* Bottom Border Accent */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 dark:via-white/15 to-transparent" />
      </div>

      {/* Content with improved styling */}
      <div className="rounded-b-2xl bg-background/95 backdrop-blur-sm">
        <OnlineUsersList
          maxHeight="450px"
          showSearch={true}
          showOnlyOnline={false}
          className="border-0 shadow-none rounded-none rounded-b-2xl"
          onUserClick={(_userId) => {
            // Add navigation logic here if needed
            // Example: navigate(`/employee/${userId}`);
          }}
        />
      </div>
    </div>
  );
});
